import { Node, mergeAttributes } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'

export interface ImageOptions {
    inline: boolean
    allowBase64: boolean
    HTMLAttributes: Record<string, any>
    onImageUpload?: (file: File) => void
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        image: {
            /**
             * Add an image
             */
            setImage: (options: { src: string; alt?: string; title?: string }) => ReturnType
        }
    }
}

export const ImageExtension = Node.create<ImageOptions>({
    name: 'image',

    addOptions() {
        return {
            inline: false,
            allowBase64: true,
            HTMLAttributes: {},
        }
    },

    inline() {
        return this.options.inline
    },

    group() {
        return this.options.inline ? 'inline' : 'block'
    },

    draggable: true,

    addAttributes() {
        return {
            src: {
                default: null,
            },
            alt: {
                default: null,
            },
            title: {
                default: null,
            },
            width: {
                default: null,
            },
            height: {
                default: null,
            },
        }
    },

    parseHTML() {
        return [
            {
                tag: 'img',
            },
        ]
    },

    renderHTML({ HTMLAttributes }) {
        return ['img', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)]
    },

    addCommands() {
        return {
            setImage:
                options =>
                ({ commands }) => {
                    return commands.insertContent({
                        type: this.name,
                        attrs: options,
                    })
                },
        }
    },

    addProseMirrorPlugins() {
        const { onImageUpload } = this.options

        return [
            new Plugin({
                key: new PluginKey('imageUpload'),
                props: {
                    handleDOMEvents: {
                        drop(view, event) {
                            const hasFiles =
                                event.dataTransfer &&
                                event.dataTransfer.files &&
                                event.dataTransfer.files.length > 0

                            if (!hasFiles) {
                                return false
                            }

                            const images = Array.from(event.dataTransfer.files).filter(file =>
                                file.type.includes('image'),
                            )

                            if (images.length === 0) {
                                return false
                            }

                            event.preventDefault()

                            const { schema } = view.state
                            const coordinates = view.posAtCoords({
                                left: event.clientX,
                                top: event.clientY,
                            })

                            images.forEach(image => {
                                // Handle image upload through parent component
                                if (onImageUpload) {
                                    onImageUpload(image)
                                } else {
                                    // Fallback: create a data URL for immediate display
                                    const reader = new FileReader()
                                    reader.onload = readerEvent => {
                                        const src = readerEvent.target?.result as string
                                        const node = schema.nodes.image.create({ src })
                                        const transaction = view.state.tr.insert(
                                            coordinates?.pos || 0,
                                            node,
                                        )
                                        view.dispatch(transaction)
                                    }
                                    reader.readAsDataURL(image)
                                }
                            })

                            return true
                        },
                        paste(view, event) {
                            const hasFiles =
                                event.clipboardData &&
                                event.clipboardData.files &&
                                event.clipboardData.files.length > 0

                            if (!hasFiles) {
                                return false
                            }

                            const images = Array.from(event.clipboardData.files).filter(file =>
                                file.type.includes('image'),
                            )

                            if (images.length === 0) {
                                return false
                            }

                            event.preventDefault()

                            const { schema } = view.state
                            const { selection } = view.state

                            images.forEach(image => {
                                // Handle image upload through parent component
                                if (onImageUpload) {
                                    onImageUpload(image)
                                } else {
                                    // Fallback: create a data URL for immediate display
                                    const reader = new FileReader()
                                    reader.onload = readerEvent => {
                                        const src = readerEvent.target?.result as string
                                        const node = schema.nodes.image.create({ src })
                                        const transaction = view.state.tr.replaceSelectionWith(node)
                                        view.dispatch(transaction)
                                    }
                                    reader.readAsDataURL(image)
                                }
                            })

                            return true
                        },
                    },
                },
            }),

            // Plugin to handle base64 image processing (similar to Quill's approach)
            new Plugin({
                key: new PluginKey('base64ImageProcessor'),
                view(editorView) {
                    let timeout: number

                    const processBase64Images = () => {
                        if (!onImageUpload) return

                        // Find all base64 images in the editor
                        const base64Images = editorView.dom.querySelectorAll('img[src^="data:"]')

                        base64Images.forEach((imgElement: HTMLImageElement) => {
                            const src = imgElement.getAttribute('src')
                            if (src && src.startsWith('data:')) {
                                // Convert base64 to blob
                                fetch(src)
                                    .then(res => res.blob())
                                    .then(blob => {
                                        // Create a file from the blob
                                        const file = new File([blob], 'pasted-image.png', {
                                            type: blob.type,
                                        })

                                        // Call the upload handler
                                        onImageUpload(file)

                                        // Remove the base64 image by finding its position in the document
                                        const { state } = editorView
                                        let found = false

                                        state.doc.descendants((node, pos) => {
                                            if (found) return false

                                            if (
                                                node.type.name === 'image' &&
                                                node.attrs.src === src
                                            ) {
                                                const transaction = state.tr.delete(
                                                    pos,
                                                    pos + node.nodeSize,
                                                )
                                                editorView.dispatch(transaction)
                                                found = true
                                                return false
                                            }
                                        })
                                    })
                                    .catch(error => {
                                        console.warn('Failed to process base64 image:', error)
                                    })
                            }
                        })
                    }

                    return {
                        update: (view, prevState) => {
                            // Only process if the document actually changed
                            if (view.state.doc.eq(prevState.doc)) {
                                return
                            }

                            clearTimeout(timeout)
                            timeout = setTimeout(() => {
                                processBase64Images()
                            }, 100) // Small delay to batch changes
                        },

                        destroy: () => {
                            clearTimeout(timeout)
                        },
                    }
                },
            }),
        ]
    },
})
